'use client';

import { usePageAction } from '@ria-client/hooks/usePageAction';
import { useNWAElementImpression } from '@ria-client/segment/hooks/useNWAElementImpression';
import type { MonetizableEnum } from '@ria-client/supergraph/marketplace/enums';
import type { RecapItem, StateAbbreviation } from '@ria-client/supergraph/marketplace/types';
import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';

export type RecapListProps = {
  title: string;
  marketplaceProductType?: string;
  recapItems: {
    recapDescription?: string;
    marketplaceEntity: {
      id: string;
      name: string;
      reviewLink?: string;
      displayName?: string;
      monetizable?: MonetizableEnum;
    };
  }[];
  state?: StateAbbreviation;
  headingComponent?: ElementType;
};

interface MarketplaceEntityWithStates {
  marketplaceEntity: { states?: StateAbbreviation[] | null };
}

interface SafeLinkWithFallbackProps extends ComponentPropsWithRef<'a'> {
  children: ReactNode;
}

function SafeLinkWithFallback({ children, href, ref, ...props }: SafeLinkWithFallbackProps) {
  return href ? (
    <a {...props} href={href} ref={ref}>
      {children}
    </a>
  ) : (
    children
  );
}

function filterEntitiesByState<T extends MarketplaceEntityWithStates>(
  items: T[],
  state?: StateAbbreviation,
): T[] {
  if (!state) return items;

  return items.filter(
    ({ marketplaceEntity: { states } }) =>
      !Array.isArray(states) || states.length === 0 || states.includes(state),
  );
}

function RecapLink({
  href,
  displayName,
  actionSection,
}: {
  href?: string;
  displayName?: string;
  actionSection: string;
}) {
  const linkCopy = displayName?.trim() || 'Review';
  const { ref, impressionId, ensureViewed } = useNWAElementImpression({
    component: 'nw_review_redirected',
    sectionName: actionSection,
    destinationUrl: href,
    linkCopy: href ? linkCopy : undefined,
  });
  const fireEventAction = usePageAction({
    actionName: 'nw_review_redirected',
    actionSection,
    actionType: 'select',
    parentEventId: impressionId,
    ...(href ? { actionDestinationUrl: href, linkCopy } : {}),
  });

  function handleClick() {
    if (!actionSection || !href) return;
    ensureViewed();
    fireEventAction({
      destination_url: href,
      link_copy: linkCopy,
    });
  }

  return (
    <SafeLinkWithFallback ref={ref} href={href} onClick={handleClick}>
      {displayName}
    </SafeLinkWithFallback>
  );
}

export default function RecapListSection({
  title,
  recapItems,
  state,
  marketplaceProductType = '',
  headingComponent: Heading = 'h3',
}: RecapListProps): React.JSX.Element {
  const geoTargetedRecap = state ? filterEntitiesByState<RecapItem>(recapItems, state) : recapItems;
  const actionSection = marketplaceProductType.toLowerCase();

  return (
    <div id="recap-section" data-testid="recap-list-section">
      <p className="typography-text">To recap our selections...</p>
      <Heading id="recap" className="typography-text-large-bold font-bold">
        {title}
      </Heading>
      <ul className="m-0 list-disc py-0 pl-6">
        {geoTargetedRecap.map(({ recapDescription, marketplaceEntity }) => {
          const { id, name, reviewLink, displayName } = marketplaceEntity;
          return (
            <li className="list-item px-0 py-1" key={`recap-list-item-${id}`}>
              <RecapLink
                href={reviewLink}
                displayName={displayName ?? name}
                actionSection={actionSection}
              />
              {recapDescription && `: ${recapDescription}`}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
